/*
  Renders one full-screen, full-bleed hero section for a single work.
  Used inside the scroll-snap container in main.js - one section per
  story/novel. Image fills the viewport, gradient overlay ensures text
  legibility, content is overlaid on top.

  config: {
    image: string (full-bleed background image URL)
    label: string (small caps eyebrow, e.g. "MYTH'S SHORTS")
    title: string (large display title)
    hook: string (one-line description)
    ctaLabel: string (button text, e.g. "ENTER")
    onEnter: function (called when CTA is clicked)
    showDownArrow: boolean (whether to show the scroll-to-next affordance)

    -- scalability additions (all optional, default to Crow's Tale's
       original behavior so existing heroes are unaffected) --
    bgSize: 'contain' | 'cover' (default 'contain')
    bgPosition: string, desktop/landscape background-position (default 'right top')
    overlayVariant: 'vertical' | 'horizontal-fade' (default 'vertical')
    showSmoke: boolean (default true - Crow's Tale keeps its smoke layer;
               set false for works that don't want the atmosphere effect)
    showBgFade: boolean (default true - portrait bottom-fade mask)
  }
*/

export function renderHeroSection(config) {
  const {
    bgSize = 'contain',
    bgPosition = 'right top',
    overlayVariant = 'vertical',
    showSmoke = true,
    showBgFade = true,
  } = config;

  const isNovelSection = config.titleStyle === 'novel';

  const section = document.createElement('section');
  section.className = isNovelSection
    ? 'relative w-full min-h-[100dvh] snap-start overflow-hidden flex items-center'
    : 'relative w-full min-h-[100dvh] snap-start overflow-hidden flex items-end';

  const bg = document.createElement('div');
  const bgSizeClass = bgSize === 'cover' ? 'bg-cover' : 'bg-contain';
  const bgFadeClass = showBgFade ? 'hero-bg-fade' : '';
  bg.className = `absolute inset-0 bg-no-repeat ${bgSizeClass} ${bgFadeClass}`.trim();
  bg.style.backgroundPosition = bgPosition;
  bg.style.backgroundImage = `url('${config.image}')`;
  if (config.mobileBgPosition) {
    bg.dataset.mobileBgPosition = config.mobileBgPosition;
    bg.classList.add('hero-bg-mobile-pos');
  }

  const overlay = document.createElement('div');
  overlay.className = overlayVariant === 'horizontal-fade'
    ? 'absolute inset-0 hero-overlay-horizontal'
    : 'absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/40';

  const isNovel = config.titleStyle === 'novel';
  const isShorts = config.titleStyle === 'shorts';

  const content = document.createElement('div');
  content.className = isNovel
    ? 'relative z-10 w-full px-6 hero-content-novel flex flex-col gap-4 max-w-2xl'
    : 'relative z-10 w-full px-6 pb-32 portrait:pb-40 landscape:pb-32 flex flex-col gap-4 max-w-lg';

  const titleFontClass = config.titleStyle === 'novel' ? 'font-novel-title' : 'font-story';
  const hookFontClass = config.titleStyle === 'novel' ? 'font-novel-hook' : '';

  const subtitleHTML = config.subtitle
    ? (isShorts
        ? `<p class="text-shorts-subtitle mb-1">${config.subtitle}</p>`
        : `<p class="${titleFontClass} text-xs uppercase tracking-[0.28em] text-neutral-400 mb-1">${config.subtitle}</p>`)
    : '';

  const eyebrowHTML = isNovel
    ? `<div class="eyebrow-wrap">
        <div class="eyebrow-line" aria-hidden="true"></div>
        <p class="text-xs font-medium uppercase tracking-[0.35em] text-novel-eyebrow">${config.label}</p>
      </div>`
    : isShorts
    ? `<div class="eyebrow-wrap">
        <div class="eyebrow-line eyebrow-line--cool" aria-hidden="true"></div>
        <p class="text-shorts-eyebrow">${config.label}</p>
      </div>`
    : `<p class="font-story text-xs uppercase tracking-[0.3em] text-neutral-300">${config.label}</p>`;

  const ctaHintHTML = config.ctaHint
    ? `<span class="text-[11px] uppercase tracking-[0.12em] text-neutral-300/70 ml-3">${config.ctaHint}</span>`
    : '';

  const titleColorClass = isNovel ? 'text-novel-cream font-normal' : (isShorts ? 'text-shorts-title' : 'text-neutral-50 font-bold');
  const titleSizeStyle = isNovel ? 'font-size:clamp(48px, 6.2vw, 84px); line-height:0.92;' : '';
  const hookColorClass = isNovel ? 'text-novel-hook' : (isShorts ? 'text-shorts-hook' : 'text-neutral-300');

  const titleHTML = config.titleAccent
    ? `<span class="block">${config.title}</span><span class="block font-novel-accent-glow">${config.titleAccent}</span>`
    : config.title;

  const hookClass = isShorts ? '' : `${hookFontClass} ${hookColorClass} text-sm max-w-xs`;
  const hookTag = isShorts ? `<p class="text-shorts-hook">${config.hook}</p>` : `<p class="${hookClass}">${config.hook}</p>`;

  content.innerHTML = `
    ${eyebrowHTML}
    <h1 class="${titleFontClass} ${titleColorClass} tracking-wide" style="${titleSizeStyle}">${titleHTML}</h1>
    ${subtitleHTML}
    ${hookTag}
    <div class="mt-2 flex items-center flex-wrap">
      <button data-role="hero-cta" class="w-fit border border-neutral-400 text-neutral-100 ${isShorts ? 'cta-shorts' : 'text-xs uppercase tracking-[0.2em]'} px-6 py-3 hover:bg-neutral-100 hover:text-neutral-900 transition">
        ${config.ctaLabel}
      </button>
      ${ctaHintHTML}
    </div>
  `;

  const ctaBtn = content.querySelector('[data-role="hero-cta"]');
  ctaBtn.addEventListener('click', () => {
    if (config.onEnter) config.onEnter();
  });

  section.appendChild(bg);

  if (showSmoke) {
    const smoke = document.createElement('div');
    smoke.className = 'hero-smoke-img';
    smoke.setAttribute('aria-hidden', 'true');
    section.appendChild(smoke);
  }

  section.append(overlay, content);

  if (config.showDownArrow) {
    const arrow = document.createElement('div');
    arrow.className = 'absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-neutral-300 animate-pulse text-2xl';
    arrow.textContent = '\u2193';
    section.appendChild(arrow);
  }

  return section;
}
